import { useEffect, useMemo, useState } from "react";
import { FaPlay, FaPlus } from "react-icons/fa";
import { Badge, Form, Modal } from "react-bootstrap";
import { fetchWithAuth } from "../api/fetchWithAuth";
import TestCaseRunFormModal from "../components/TestCaseRunFormModal";

const resultOptions = [
  { value: "passed", label: "Passed", variant: "success", color: "#198754" },
  { value: "failed", label: "Failed", variant: "danger", color: "#dc3545" },
  { value: "blocked", label: "Blocked", variant: "warning", color: "#fdc43f" },
  { value: "not_run", label: "Not run", variant: "secondary", color: "#adb5bd" },
  { value: "inconclusive", label: "Inconclusive", variant: "info", color: "#0dcaf0" }
];

function formatDateTime(value) {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";

  return date.toLocaleString("bg-BG", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit"
  });
}

function getResultOption(result) {
  return resultOptions.find((option) => option.value === result) ?? {
    value: result,
    label: result || "-",
    variant: "secondary"
  };
}

function ResultBadge({ result }) {
  const option = getResultOption(result);
  return <Badge bg={option.variant}>{option.label}</Badge>;
}

function sortByName(items) {
  return [...items].sort((left, right) => (
    String(left.name ?? "").localeCompare(
      String(right.name ?? ""),
      undefined,
      { numeric: true, sensitivity: "base" }
    )
  ));
}

function sortVersions(versions) {
  return [...versions].sort((leftVersion, rightVersion) => {
    const activeCompare = Number(Boolean(rightVersion.isActive)) - Number(Boolean(leftVersion.isActive));
    if (activeCompare !== 0) return activeCompare;

    const closedCompare = Number(Boolean(leftVersion.closed)) - Number(Boolean(rightVersion.closed));
    if (closedCompare !== 0) return closedCompare;

    return String(leftVersion.name ?? "").localeCompare(
      String(rightVersion.name ?? ""),
      undefined,
      { numeric: true, sensitivity: "base" }
    );
  });
}

function getVersionMajor(versionName) {
  const match = String(versionName ?? "").trim().match(/^(\d+)\./);
  return match?.[1] ?? null;
}

function buildVersionFilterOptions(versions) {
  const sortedVersions = sortVersions(versions);
  const majorCounts = sortedVersions.reduce((counts, version) => {
    const major = getVersionMajor(version.name);
    if (major) counts[major] = (counts[major] ?? 0) + 1;
    return counts;
  }, {});
  const majorOptions = Object.entries(majorCounts)
    .filter(([, count]) => count > 1)
    .map(([major]) => ({
      value: `major:${major}`,
      label: `${major}.x`,
      isMajor: true,
      major
    }))
    .sort((left, right) => left.major.localeCompare(right.major, undefined, { numeric: true }));

  return [
    ...majorOptions,
    ...sortedVersions.map((version) => ({
      value: String(version.id),
      label: version.name,
      isMajor: false,
      version
    }))
  ];
}

function buildResultSegments(results) {
  const total = results.length;

  return resultOptions
    .map((option) => {
      const count = results.filter((result) => result.result === option.value).length;
      const percent = total > 0 ? (count / total) * 100 : 0;

      return {
        ...option,
        count,
        percent
      };
    })
    .filter((segment) => segment.count > 0);
}

function buildDonutBackground(segments) {
  const stops = [];
  let currentOffset = 0;

  segments.forEach((segment) => {
    const start = currentOffset;
    const end = currentOffset + segment.percent;
    stops.push(`${segment.color} ${start}% ${end}%`);
    currentOffset = end;
  });

  return segments.length ? `conic-gradient(${stops.join(", ")})` : "#e9ecef";
}

function ResultSummary({ results }) {
  const segments = buildResultSegments(results);
  const donutBackground = buildDonutBackground(segments);

  return (
    <div className="border rounded p-3 bg-white mb-3">
      <div className="d-flex justify-content-between align-items-center gap-3 flex-wrap mb-3">
        <div>
          <div className="fw-semibold">Result distribution</div>
          <div className="text-muted small">Real runs plus not-run test cases for the current filters.</div>
        </div>
        <span className="badge text-bg-secondary">Total: {results.length}</span>
      </div>

      <div className="d-flex align-items-start gap-4 flex-wrap">
        <div className="d-flex align-items-center gap-3">
          <div className="position-relative" style={{ width: "112px", height: "112px", flex: "0 0 auto" }}>
            <div
              style={{
                width: "112px",
                height: "112px",
                borderRadius: "50%",
                background: donutBackground,
                boxShadow: "inset 0 0 0 1px rgba(0, 0, 0, 0.06)"
              }}
            />
            <div
              className="position-absolute top-50 start-50 translate-middle rounded-circle bg-white d-flex flex-column align-items-center justify-content-center"
              style={{
                width: "64px",
                height: "64px",
                boxShadow: "0 0 0 1px rgba(0, 0, 0, 0.04)"
              }}
            >
              <div className="fw-semibold" style={{ fontSize: "1.1rem", lineHeight: 1 }}>
                {results.length}
              </div>
              <div className="text-muted" style={{ fontSize: "0.68rem", lineHeight: 1.1 }}>
                results
              </div>
            </div>
          </div>

          <div className="d-flex flex-column gap-2 small">
            {segments.length ? segments.map((segment) => (
              <div key={segment.value} className="d-inline-flex align-items-center gap-2">
                <span
                  style={{
                    width: "10px",
                    height: "10px",
                    borderRadius: "999px",
                    display: "inline-block",
                    backgroundColor: segment.color
                  }}
                />
                <span>
                  {segment.label}: {segment.count} ({Math.round(segment.percent)}%)
                </span>
              </div>
            )) : (
              <span className="text-muted">No results yet.</span>
            )}
          </div>
        </div>

        <div className="flex-grow-1" style={{ minWidth: "260px" }}>
          <div
            style={{
              height: "14px",
              borderRadius: "999px",
              overflow: "hidden",
              backgroundColor: "#e9ecef",
              display: "flex"
            }}
          >
            {segments.map((segment) => (
              <div
                key={segment.value}
                title={`${segment.label}: ${segment.count} (${Math.round(segment.percent)}%)`}
                style={{
                  width: `${segment.percent}%`,
                  backgroundColor: segment.color,
                  minWidth: segment.percent > 0 ? "6px" : "0"
                }}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function SelectTestCaseModal({ show, onClose, onSelect, testCases }) {
  const [testCaseId, setTestCaseId] = useState("");

  useEffect(() => {
    if (!show) {
      setTestCaseId("");
      return;
    }

    setTestCaseId((current) => current || testCases[0]?.id || "");
  }, [show, testCases]);

  const selectedTestCase = testCases.find((testCase) => String(testCase.id) === String(testCaseId));

  return (
    <Modal show={show} onHide={onClose}>
      <Modal.Header closeButton>
        <Modal.Title>Select test case</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <Form.Group>
          <Form.Label>Test case</Form.Label>
          <Form.Select
            value={testCaseId}
            onChange={(event) => setTestCaseId(event.target.value)}
          >
            {testCases.map((testCase) => (
              <option key={testCase.id} value={testCase.id}>
                {testCase.number} - {testCase.name}
              </option>
            ))}
          </Form.Select>
        </Form.Group>
      </Modal.Body>

      <Modal.Footer>
        <button type="button" className="btn btn-secondary" onClick={onClose}>
          Cancel
        </button>
        <button
          type="button"
          className="btn btn-success"
          disabled={!selectedTestCase}
          onClick={() => selectedTestCase && onSelect(selectedTestCase)}
        >
          Continue
        </button>
      </Modal.Footer>
    </Modal>
  );
}

export default function TestResults({ user }) {
  const [runs, setRuns] = useState([]);
  const [versions, setVersions] = useState([]);
  const [devices, setDevices] = useState([]);
  const [testCases, setTestCases] = useState([]);
  const [versionFilter, setVersionFilter] = useState("all");
  const [deviceFilter, setDeviceFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [error, setError] = useState("");
  const [selectingTestCase, setSelectingTestCase] = useState(false);
  const [runFormTestCase, setRunFormTestCase] = useState(null);
  const [runFormInitialValues, setRunFormInitialValues] = useState({});

  const roleCodes = user?.roles ?? [];
  const canManage = roleCodes.includes("PM") || roleCodes.includes("TST");

  const sortedVersions = useMemo(() => sortVersions(versions), [versions]);
  const versionFilterOptions = useMemo(() => buildVersionFilterOptions(versions), [versions]);
  const sortedDevices = useMemo(() => sortByName(devices), [devices]);
  const sortedTestCases = useMemo(() => (
    [...testCases].sort((left, right) => (
      String(left.number ?? "").localeCompare(
        String(right.number ?? ""),
        undefined,
        { numeric: true, sensitivity: "base" }
      )
    ))
  ), [testCases]);

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      setDebouncedSearch(search);
    }, 180);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [search]);

  const loadLookups = async () => {
    try {
      const [versionsRes, devicesRes, testCasesRes] = await Promise.all([
        fetchWithAuth("/api/versions"),
        fetchWithAuth("/api/devices"),
        fetchWithAuth("/api/test-cases?compact=1")
      ]);

      if (!versionsRes.ok || !devicesRes.ok || !testCasesRes.ok) {
        throw new Error("Failed to load filters");
      }

      setVersions(await versionsRes.json());
      setDevices(await devicesRes.json());
      setTestCases(await testCasesRes.json());
    } catch (err) {
      setVersions([]);
      setDevices([]);
      setTestCases([]);
      setError(err.message);
    }
  };

  const loadRuns = async () => {
    try {
      const params = new URLSearchParams();
      if (versionFilter.startsWith("major:")) {
        params.set("versionMajor", versionFilter.replace("major:", ""));
      } else if (versionFilter !== "all") {
        params.set("versionId", versionFilter);
      }
      if (deviceFilter !== "all") params.set("deviceId", deviceFilter);

      const query = params.toString();
      const res = await fetchWithAuth(`/api/test-cases/runs${query ? `?${query}` : ""}`);

      if (!res.ok) {
        const responseError = await res.json().catch(() => null);
        throw new Error(responseError?.error || "Failed to load SW test reports");
      }

      const data = await res.json();
      setRuns(data.rows ?? []);
      setError("");
    } catch (err) {
      setRuns([]);
      setError(err.message);
    }
  };

  useEffect(() => {
    loadLookups();
  }, []);

  useEffect(() => {
    loadRuns();
  }, [versionFilter, deviceFilter]);

  const displayResults = useMemo(() => {
    const selectedVersion = versionFilter.startsWith("major:")
      ? { name: `${versionFilter.replace("major:", "")}.x` }
      : sortedVersions.find((version) => String(version.id) === String(versionFilter)) ?? null;
    const selectedDevice = sortedDevices.find((device) => String(device.id) === String(deviceFilter)) ?? null;
    const runsByTestCaseId = new Set(runs.map((run) => Number(run.testCaseId)));
    const missingResults = sortedTestCases
      .filter((testCase) => !runsByTestCaseId.has(Number(testCase.id)))
      .map((testCase) => ({
        id: `not-run-${testCase.id}`,
        synthetic: true,
        TestCase: testCase,
        Version: selectedVersion,
        Device: selectedDevice,
        runNo: "-",
        result: "not_run",
        executedAt: null,
        source: "-",
        durationMs: null,
        note: "",
        log: ""
      }));

    return [...runs, ...missingResults].sort((left, right) => (
      String(left.TestCase?.number ?? "").localeCompare(
        String(right.TestCase?.number ?? ""),
        undefined,
        { numeric: true, sensitivity: "base" }
      ) || Number(Boolean(left.synthetic)) - Number(Boolean(right.synthetic)) ||
      Number(right.runNo || 0) - Number(left.runNo || 0)
    ));
  }, [deviceFilter, runs, sortedDevices, sortedTestCases, sortedVersions, versionFilter]);

  const filteredResults = displayResults.filter((run) => {
    const query = debouncedSearch.trim().toLowerCase();
    if (!query) return true;

    return (
      String(run.TestCase?.number ?? "").toLowerCase().includes(query) ||
      String(run.TestCase?.name ?? "").toLowerCase().includes(query) ||
      String(run.TestCase?.type ?? "").toLowerCase().includes(query) ||
      String(run.Version?.name ?? "").toLowerCase().includes(query) ||
      String(run.Device?.name ?? "").toLowerCase().includes(query) ||
      String(run.result ?? "").toLowerCase().includes(query) ||
      String(run.note ?? "").toLowerCase().includes(query) ||
      String(run.log ?? "").toLowerCase().includes(query)
    );
  });

  const stickyHeaderStyle = {
    position: "sticky",
    top: 0,
    zIndex: 1,
    backgroundColor: "var(--bs-table-bg, #f8f9fa)"
  };

  const getRowStyle = (index) => ({
    "--bs-table-bg": index % 2 === 0 ? "rgba(25, 135, 84, 0.06)" : "#ffffff"
  });

  const getFilterInitialValues = () => ({
    versionId: versionFilter === "all" || versionFilter.startsWith("major:") ? "" : versionFilter,
    deviceId: deviceFilter === "all" ? "" : deviceFilter
  });

  const openRunForm = (testCase) => {
    setRunFormInitialValues(getFilterInitialValues());
    setRunFormTestCase(testCase);
  };

  return (
    <div className="container mt-4">
      <div className="d-flex justify-content-between align-items-center mb-3 gap-3 flex-wrap">
        <div className="d-flex align-items-center gap-2 flex-wrap">
          <select
            className="form-select"
            value={versionFilter}
            onChange={(event) => setVersionFilter(event.target.value)}
            style={{ width: "180px" }}
            aria-label="Filter by SW version"
          >
            <option value="all">All SW versions</option>
            {versionFilterOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>

          <select
            className="form-select"
            value={deviceFilter}
            onChange={(event) => setDeviceFilter(event.target.value)}
            style={{ width: "180px" }}
            aria-label="Filter by device"
          >
            <option value="all">All devices</option>
            {sortedDevices.map((device) => (
              <option key={device.id} value={device.id}>
                {device.name}
              </option>
            ))}
          </select>

          <div className="position-relative" style={{ width: "320px", maxWidth: "100%" }}>
            <input
              type="text"
              className="form-control pe-5"
              placeholder="Search SW test reports..."
              value={search}
              onChange={(event) => setSearch(event.target.value)}
            />
            {search && (
              <button
                type="button"
                className="btn btn-sm position-absolute top-50 end-0 translate-middle-y me-2 border-0 bg-transparent text-muted"
                onClick={() => {
                  setSearch("");
                  setDebouncedSearch("");
                }}
                aria-label="Clear search"
              >
                x
              </button>
            )}
          </div>

          <span className="badge text-bg-secondary">
            Total: {filteredResults.length}
          </span>
        </div>

        {canManage && (
          <button
            type="button"
            className="btn btn-success"
            onClick={() => setSelectingTestCase(true)}
          >
            <FaPlay className="me-1" />
            Add run
          </button>
        )}
      </div>

      {error && <div className="alert alert-danger py-2">{error}</div>}

      <ResultSummary results={filteredResults} />

      <div style={{ maxHeight: "60vh", overflowY: "auto" }}>
        <table className="table table-hover align-middle mb-0">
          <thead className="table-light">
            <tr>
              <th style={{ ...stickyHeaderStyle, width: "140px" }}>Test case</th>
              <th style={{ ...stickyHeaderStyle, minWidth: "220px" }}>Name</th>
              <th style={{ ...stickyHeaderStyle, width: "130px" }}>Version</th>
              <th style={{ ...stickyHeaderStyle, width: "130px" }}>Device</th>
              <th style={{ ...stickyHeaderStyle, width: "80px" }}>Run #</th>
              <th style={{ ...stickyHeaderStyle, width: "110px" }}>Result</th>
              <th style={{ ...stickyHeaderStyle, width: "170px" }}>Executed</th>
              <th style={{ ...stickyHeaderStyle, width: "110px" }}>Source</th>
              <th style={{ ...stickyHeaderStyle, width: "110px" }}>Duration</th>
              <th style={stickyHeaderStyle}>Note / Log</th>
              {canManage && <th style={{ ...stickyHeaderStyle, width: "90px" }}>Actions</th>}
            </tr>
          </thead>

          <tbody>
            {filteredResults.length === 0 ? (
              <tr>
                <td colSpan={canManage ? 11 : 10} className="text-center text-muted py-4">
                  No SW test reports found.
                </td>
              </tr>
            ) : filteredResults.map((run, index) => (
              <tr key={run.id} style={getRowStyle(index)}>
                <td className="fw-semibold">{run.TestCase?.number ?? "-"}</td>
                <td>{run.TestCase?.name ?? "-"}</td>
                <td>{run.Version?.name ?? "-"}</td>
                <td>{run.Device?.name ?? "-"}</td>
                <td>{run.runNo}</td>
                <td><ResultBadge result={run.result} /></td>
                <td>{formatDateTime(run.executedAt)}</td>
                <td>{run.source}</td>
                <td>{run.durationMs === null || run.durationMs === undefined ? "-" : run.durationMs}</td>
                <td>
                  {run.note && <div>{run.note}</div>}
                  {run.log && (
                    <details className="mt-1">
                      <summary className="text-primary" style={{ cursor: "pointer" }}>Log</summary>
                      <pre className="small bg-light border rounded p-2 mt-2 mb-0" style={{ whiteSpace: "pre-wrap" }}>
                        {run.log}
                      </pre>
                    </details>
                  )}
                  {!run.note && !run.log && <span className="text-muted">-</span>}
                </td>
                {canManage && (
                  <td>
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-success"
                      onClick={() => openRunForm(run.TestCase)}
                      title="Add run"
                    >
                      <FaPlus />
                    </button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <SelectTestCaseModal
        show={selectingTestCase}
        onClose={() => setSelectingTestCase(false)}
        testCases={sortedTestCases}
        onSelect={(testCase) => {
          setSelectingTestCase(false);
          openRunForm(testCase);
        }}
      />

      <TestCaseRunFormModal
        show={Boolean(runFormTestCase)}
        onClose={() => setRunFormTestCase(null)}
        onSaved={loadRuns}
        testCase={runFormTestCase}
        initialValues={runFormInitialValues}
      />
    </div>
  );
}
